import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostListener,
    inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    AppValidators,
    filterNulls,
    fromControl,
    HiddenValueWhileLoading,
    switchMapWithLoading,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, map, Observable} from 'rxjs';
import {selectBuilding} from 'src/app/state/selectors';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {SelectMetersForVariableAlarmsComponent} from './select-meters-for-variable-alarms/select-meters-for-variable-alarms.component';
import {
    VariableAlarmSettings,
    VariableAlarmTemplate,
} from '../variable-alarm-type-eligibility.model';
import {templates} from './variable-alarm-item-lists';
import {VariableAlarmsService} from '../../variable-alarms.service';
import {VariableAlarm, VariableAlarmEdit} from '../../variable-alarm.model';
import {DateTime} from 'luxon';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {VariableAlarmSettingsFormGroup} from './variable-alarm-settings.model';
import {
    CheckboxGroupComponent,
    ControlMessagesComponent,
    InputEmailListComponent,
    InputSelectOptionGroupComponent,
    LoadComponent,
    TimePipe,
} from '@brunata-angular/ui';
import {
    MonitoringMeterBase,
    MonitoringMeterWithTranslations,
} from '../../../monitoring-meter.model';
import {InputNumberGroupComponent} from '@brunata-angular/ui';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {shareReplay, startWith, switchMap, take, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {HasUnsavedChanges} from '@brunata-angular/ui';
import {getTemplates} from '../templates';

type UsageUnitWithTranslation = {
    usageUnitTranslated: string;
    usage: string;
    unit: string;
};
type View = {
    usageUnitWithTranslation: UsageUnitWithTranslation[];
    buildingNo: string;
    metersForAgents: MonitoringMeterWithTranslations[];
};

@Component({
    selector: 'app-variable-alarm-settings',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        InputTextGroupComponent,
        InputNumberGroupComponent,
        ReactiveFormsModule,
        InputSelectOptionGroupComponent,
        RouterLink,
        ControlMessagesComponent,
        LoadComponent,
        InputEmailListComponent,
        CheckboxGroupComponent,
    ],
    providers: [TimePipe],
    templateUrl: './variable-alarm-settings.component.html',
    styleUrl: './variable-alarm-settings.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariableAlarmSettingsComponent implements HasUnsavedChanges {
    variableAlarmTemplate: VariableAlarmTemplate = {
        type: '',
        translation: {
            header: '',
            description: '',
        },
        variableAlarmEligibility: true,
    };

    protected readonly variableAlarmToEditSubject: BehaviorSubject<VariableAlarm | null> =
        new BehaviorSubject<VariableAlarm | null>(null);
    private variableAlarmsService = inject(VariableAlarmsService);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly store = inject(Store);
    private readonly toastrService = inject(ToastrService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly variableAlarmService = inject(VariableAlarmsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly timePipe = inject(TimePipe);
    private readonly changeDetectionRef = inject(ChangeDetectorRef);

    protected readonly usageAndUnitMap: Map<string, string> = new Map<string, string>([]);
    protected readonly unitFromUsageUnit: Map<string, string> = new Map<string, string>([]);
    protected readonly periodicallyTimes: Map<string, string> = new Map<string, string>();
    protected readonly variableAlarmSettings: Map<string, VariableAlarmSettings> = templates;
    private readonly buildingNo$ = this.store.select(selectBuilding).pipe(
        map(state => (state.status === 'success' ? state.buildingNo : null)),
        filterNulls(),
    );
    readonly deviationNotificRuleId$: Observable<string> = this.route.paramMap.pipe(
        map(params => params.get('deviationNotificRuleId')),
        filterNulls(),
    );
    readonly templateType$: Observable<{
        type: string;
        translation: {
            header: string;
            description: string;
        };
        variableAlarmEligibility: boolean;
    }> = this.route.paramMap.pipe(
        map(params => {
            const template = getTemplates(this.translateService).find(
                it => it.type === params.get('templateType'),
            );
            return template ?? null;
        }),
        filterNulls(),
    );

    protected formGroup: VariableAlarmSettingsFormGroup = this.formBuilder.group({
        alarmName: new FormControl<string | null>('', {
            validators: [Validators.required, Validators.maxLength(200)],
        }),
        usageAndUnit: new FormControl<string | null>(null, Validators.required),
        meterIds: this.formBuilder.array<FormControl<string>>([], Validators.required),
        deviationType: new FormControl<string | null>('I', Validators.required),
        deviationValue: new FormControl<number | null>(null, [
            Validators.required,
            AppValidators.preciseNumber(10, 5),
        ]),
        minimumDeviationPeriod: new FormControl<number | null>(null, [
            Validators.required,
            AppValidators.numberBetween(0, 100, 'hourlyPeriodBetween'),
        ]),
        notifyInSpecificTimePeriods: new FormControl<boolean>(false),
        throttle: new FormControl<number | null>({value: null, disabled: true}, [
            Validators.required,
            AppValidators.numberBetween(0, 999, 'hourlyPeriodBetween'),
        ]),
        notificationBreaks: new FormControl<boolean>(false),
        startTime: new FormControl<string | null>('00:00', Validators.required),
        endTime: new FormControl<string | null>('00:00', Validators.required),
        recipients: new FormControl<string[]>([], Validators.required),
    });

    readonly variableAlarmToEdit$: Observable<VariableAlarm | null> = combineLatest([
        this.buildingNo$,
        this.deviationNotificRuleId$,
    ]).pipe(
        switchMap(([buildingNo, deviationNotificRuleId]) =>
            this.variableAlarmsService.getVariableAlarm(buildingNo, deviationNotificRuleId).pipe(
                map(variableAlarm => ({
                    ...variableAlarm,
                    alarmName: variableAlarm.alarmName,
                    deviationNotificRuleId,
                })),
            ),
        ),
        shareReplay(1),
    );
    readonly view$: Observable<HiddenValueWhileLoading<View>> = combineLatest([
        this.buildingNo$,
        this.variableAlarmToEdit$.pipe(startWith(null)),
    ]).pipe(
        switchMapWithLoading(([buildingNo, variableAlarmToEdit]) =>
            this.variableAlarmService
                .getEligibleMeters(buildingNo, variableAlarmToEdit?.deviationNotificRuleId)
                .pipe(
                    map(metersForAgents => ({
                        buildingNo,
                        usageUnitWithTranslation: metersForAgents.map(metersForAgent => ({
                            usageUnitTranslated:
                                this.createUsageUnitWithTranslationMap(metersForAgent),
                            usage: metersForAgent.alloUnitType,
                            unit: metersForAgent.unit,
                        })),
                        metersForAgents,
                    })),
                    tap(() => this.patchVariableAlarmTemplateSettings(this.formGroup)),
                ),
        ),
    );

    constructor() {
        this.createCustomTemplate();
        this.variableAlarmToEdit$.pipe(take(1)).subscribe(variableAlarm => {
            this.variableAlarmToEditSubject.next(variableAlarm);
        });

        for (let hour = 0; hour < 24; hour++) {
            this.periodicallyTimes.set(
                this.timePipe.transform(DateTime.fromObject({hour})),
                this.timePipe.transform(DateTime.fromObject({hour})),
            );
        }

        this.templateType$.pipe(take(1)).subscribe(template => {
            this.variableAlarmTemplate = template;
        });
        fromControl(this.formGroup.controls.notificationBreaks)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                if (value === true) {
                    this.formGroup.controls.throttle.enable();
                } else {
                    this.formGroup.controls.throttle.disable();
                }
            });
    }

    private createCustomTemplate(): void {
        const template: VariableAlarmTemplate = {
            type: 'customAlarm',
            translation: {
                header: this.translateService.translate('variable-alarms_Custom-alarm'),
                description: this.translateService.translate('variable-alarms_customAlarm'),
            },
            variableAlarmEligibility: true,
        };
        this.variableAlarmTemplate = template;
    }

    private createUsageUnitWithTranslationMap(metersForAgent: MonitoringMeterBase): string {
        const allocationUnit = metersForAgent.alloUnitType
            ? this.translateService.translate(
                  'mappers_allocationUnitMap.' + metersForAgent.alloUnitType,
                  'online',
                  'common',
              )
            : '';

        const unit = metersForAgent.unit
            ? this.translateService.translate(
                  'mappers_measurementUnit.' + metersForAgent.unit,
                  'online',
                  'common',
              )
            : '';
        const usageUnitTranslated = allocationUnit + ' - ' + unit;
        this.usageAndUnitMap.set(usageUnitTranslated, usageUnitTranslated);
        this.unitFromUsageUnit.set(usageUnitTranslated, unit);
        return usageUnitTranslated;
    }

    private patchVariableAlarmTemplateSettings(formGroup: VariableAlarmSettingsFormGroup): void {
        if (this.variableAlarmTemplate.type !== 'customAlarm') {
            const settings = this.variableAlarmSettings.get(this.variableAlarmTemplate.type);
            let usageUnitTranslatedFromSettings: string | null = null;
            if (settings != null && this.variableAlarmToEditSubject.value === null) {
                const allocationUnitFromSettings =
                    settings.allocationUnit != null
                        ? this.translateService.translate(
                              'mappers_allocationUnitMap.' + settings.allocationUnit,
                              'online',
                              'common',
                          )
                        : '';

                const unitFromSettings =
                    settings.unit != null
                        ? this.translateService.translate(
                              'mappers_measurementUnit.' + settings.unit,
                              'online',
                              'common',
                          )
                        : '';
                usageUnitTranslatedFromSettings =
                    allocationUnitFromSettings + ' - ' + unitFromSettings;

                this.usageAndUnitMap.set(
                    usageUnitTranslatedFromSettings,
                    usageUnitTranslatedFromSettings,
                );
                this.unitFromUsageUnit.set(usageUnitTranslatedFromSettings, unitFromSettings);
            } else if (this.variableAlarmToEditSubject.value !== null) {
                const variableAlarm = this.variableAlarmToEditSubject.value;
                const allocationUnitFromSettings = variableAlarm.allocationUnit
                    ? this.translateService.translate(
                          'mappers_allocationUnitMap.' + variableAlarm.allocationUnit,
                          'online',
                          'common',
                      )
                    : '';

                const unitFromSettings =
                    variableAlarm.unit != null
                        ? this.translateService.translate(
                              'mappers_measurementUnit.' + variableAlarm.unit,
                              'online',
                              'common',
                          )
                        : '';
                usageUnitTranslatedFromSettings =
                    allocationUnitFromSettings + ' - ' + unitFromSettings;

                this.usageAndUnitMap.set(
                    usageUnitTranslatedFromSettings,
                    usageUnitTranslatedFromSettings,
                );
                this.unitFromUsageUnit.set(usageUnitTranslatedFromSettings, unitFromSettings);
            }

            formGroup.patchValue(
                {
                    deviationType: settings?.deviationType ?? null,
                    deviationValue: settings?.deviationValue ?? null,
                    minimumDeviationPeriod: (settings?.minimumDeviationPeriod ?? 0) / 60,
                    usageAndUnit: settings != null ? usageUnitTranslatedFromSettings : null,
                },
                {emitEvent: false},
            );

            if (this.variableAlarmTemplate.type !== 'customAlarm') {
                formGroup.controls.usageAndUnit.disable({emitEvent: false});
            }
        }
        this.patchValuesForEditableVariableAlarm(formGroup);
    }

    private patchValuesForEditableVariableAlarm(formGroup: VariableAlarmSettingsFormGroup): void {
        if (this.variableAlarmToEditSubject.value == null) {
            return;
        }

        const variableAlarm = this.variableAlarmToEditSubject.value;

        const allocationUnitFromSettings = variableAlarm.allocationUnit
            ? this.translateService.translate(
                  'mappers_allocationUnitMap.' + variableAlarm.allocationUnit,
                  'online',
                  'common',
              )
            : '';

        const unitFromSettings =
            variableAlarm.unit != null
                ? this.translateService.translate(
                      'mappers_measurementUnit.' + variableAlarm.unit,
                      'online',
                      'common',
                  )
                : '';
        const usageUnitTranslatedFromSettings =
            allocationUnitFromSettings + ' - ' + unitFromSettings;

        this.usageAndUnitMap.set(usageUnitTranslatedFromSettings, usageUnitTranslatedFromSettings);
        formGroup.patchValue(
            {
                alarmName: variableAlarm.alarmName,
                usageAndUnit: usageUnitTranslatedFromSettings,
                deviationType: variableAlarm.deviationType,
                deviationValue: variableAlarm.deviationValue,
                minimumDeviationPeriod:
                    variableAlarm.minimumDeviationPeriod !== null
                        ? parseFloat((variableAlarm.minimumDeviationPeriod / 60).toFixed(2))
                        : null,
                startTime: variableAlarm.activeStartTime,
                endTime: variableAlarm.activeEndTime,
                recipients: variableAlarm.recipients.filter(recipient => recipient !== null),
                notifyInSpecificTimePeriods:
                    variableAlarm.activeStartTime !== '00:00' ||
                    variableAlarm.activeEndTime !== '00:00',
                notificationBreaks: variableAlarm.throttle !== null && variableAlarm.throttle > 0,

                throttle:
                    variableAlarm.throttle !== null
                        ? parseFloat((variableAlarm.throttle / 60).toFixed(2))
                        : null,
            },
            {emitEvent: false},
        );
        if (formGroup.controls.notificationBreaks.value === true) {
            formGroup.controls.throttle.enable({emitEvent: false});
        }
        formGroup.controls.meterIds.clear({emitEvent: false});
        variableAlarm.meterIds.forEach(meter => {
            formGroup.controls.meterIds.push(this.formBuilder.control(meter), {emitEvent: false});
        });

        formGroup.controls.usageAndUnit.disable({emitEvent: false});
    }

    protected submit(
        buildingNo: string,
        usageUnitWithTranslation: UsageUnitWithTranslation[],
        formGroup: VariableAlarmSettingsFormGroup,
    ): void {
        formGroup.markAllAsTouched();

        if (formGroup.invalid) {
            return;
        }

        const usageUnit = usageUnitWithTranslation.find(
            it => it.usageUnitTranslated === formGroup.getRawValue().usageAndUnit,
        );
        const formValues = formGroup.getRawValue();
        const dataToSend: VariableAlarmEdit = {
            deviationType: formValues.deviationType,
            deviationValue: formValues.deviationValue,
            minimumDeviationPeriod:
                formValues.minimumDeviationPeriod !== null
                    ? formValues.minimumDeviationPeriod * 60
                    : null,
            activeStartTime: formValues.startTime,
            activeEndTime: formValues.endTime,
            throttle: formValues.notificationBreaks === true ? (formValues.throttle ?? 0) * 60 : 0,
            alarmName: formValues.alarmName,
            recipients: formValues.recipients ?? [],
            meterIds: formValues.meterIds.map((meterId: string) => meterId),
        };

        if (this.variableAlarmToEditSubject.value !== null) {
            this.variableAlarmService
                .editVariableAlarm(
                    buildingNo,
                    this.variableAlarmToEditSubject.value.deviationNotificRuleId,
                    dataToSend,
                )
                .subscribe(() => {
                    formGroup.markAsUntouched();
                    this.toastrService.success(
                        this.translateService
                            .translate('variable-alarms_updated')
                            .replace('{0}', formValues.alarmName ?? ''),
                    );
                    void this.router.navigate([
                        `/admin/system/${buildingNo}/monitoring/variable-alarms`,
                    ]);
                });
        } else {
            this.variableAlarmService
                .createVariableAlarm(buildingNo, {
                    allocationUnit: usageUnit?.usage ?? null,
                    unit: Number(usageUnit?.unit),
                    ...dataToSend,
                })
                .subscribe(() => {
                    formGroup.markAsUntouched();
                    this.toastrService.success(
                        this.translateService
                            .translate('variable-alarms_created')
                            .replace('{0}', formValues.alarmName ?? ''),
                    );
                    void this.router.navigate([
                        `/admin/system/${buildingNo}/monitoring/variable-alarms`,
                    ]);
                });
        }
    }

    protected resetValues(formGroup: VariableAlarmSettingsFormGroup): void {
        formGroup.reset({
            alarmName: '',
            usageAndUnit: null,
            deviationType: 'I',
            startTime: '00:00',
            endTime: '00:00',
        });
        this.formGroup.controls.meterIds.clear();

        if (this.variableAlarmTemplate.type !== 'customAlarm') {
            this.patchVariableAlarmTemplateSettings(formGroup);
        }
    }

    protected selectMeters(
        formGroup: VariableAlarmSettingsFormGroup,
        metersForAgents: MonitoringMeterWithTranslations[],
    ): void {
        const modalRef = this.modalService.open(SelectMetersForVariableAlarmsComponent, {
            size: 'lg',
            backdrop: 'static',
        });
        const instance = modalRef.componentInstance as SelectMetersForVariableAlarmsComponent;
        instance.usageAndUnit = formGroup.getRawValue().usageAndUnit ?? '';
        instance.meterIds = formGroup.controls.meterIds;
        instance.fromTemplate = this.variableAlarmTemplate.type !== 'customAlarm';
        instance.metersForAgents = metersForAgents;
        instance.submitSelectedMeters.pipe(take(1)).subscribe(meterIds => {
            this.formGroup.controls.meterIds = this.formBuilder.array<FormControl<string>>(
                meterIds.map(meterId => this.formBuilder.control<string>(meterId)),
            );
            this.changeDetectionRef.markForCheck();
            this.formGroup.updateValueAndValidity();
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        // Display confirmation dialog
        event.preventDefault();
        event.returnValue = ''; // Chrome requires this to be set
    }

    hasUnsavedChanges(): boolean {
        return this.formGroup.touched && this.formGroup.dirty;
    }
}
